import React from 'react';
import { Activity, BarChart3, CheckCircle2, Users } from 'lucide-react';

const QuestionDisplay = ({
  questionNumber,
  submittedAnswerCount,
  attendanceCount,
  answersData,
  questionType,
  canvasRef,
  isPollActive,
  showPromptModal,
  pollTimer,
}) => {
  const submissionRate = attendanceCount > 0 ? ((submittedAnswerCount / attendanceCount) * 100).toFixed(1) : 0;
  const hasResponses = Object.values(answersData || {}).some((count) => count > 0);
  const statusLabel = isPollActive ? 'Collecting responses' : showPromptModal ? 'Choose correct answer' : 'Ready for next poll';

  return (
    <div
      className="rounded-lg border p-4"
      style={{
        backgroundColor: 'var(--card-bg)',
        borderColor: 'var(--border-color)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em]" style={{ color: 'var(--primary-color)' }}>
            Live results
          </p>
          <h4 className="mt-1 text-xl font-semibold" style={{ color: 'var(--text-color)' }}>
            Question #{questionNumber}
          </h4>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted-color)' }}>
            {statusLabel}
          </p>
        </div>
        <div
          className="inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold"
          style={{
            borderColor: isPollActive ? 'var(--success-color)' : 'var(--border-color)',
            color: isPollActive ? 'var(--success-color)' : 'var(--muted-color)',
            backgroundColor: 'var(--alt-bg)',
          }}
        >
          <Activity size={14} aria-hidden="true" />
          {isPollActive ? `${pollTimer}s live` : 'Not live'}
        </div>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border p-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--alt-bg)' }}>
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--text-color)' }}>
            <CheckCircle2 size={16} aria-hidden="true" />
            Submitted
          </div>
          <div className="mt-2 text-2xl font-bold" style={{ color: 'var(--text-color)' }}>{submittedAnswerCount}</div>
        </div>
        <div className="rounded-lg border p-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--alt-bg)' }}>
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--text-color)' }}>
            <Users size={16} aria-hidden="true" />
            Attendance
          </div>
          <div className="mt-2 text-2xl font-bold" style={{ color: 'var(--text-color)' }}>{attendanceCount}</div>
        </div>
        <div className="rounded-lg border p-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--alt-bg)' }}>
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--text-color)' }}>
            <BarChart3 size={16} aria-hidden="true" />
            Rate
          </div>
          <div className="mt-2 text-2xl font-bold" style={{ color: 'var(--text-color)' }}>{submissionRate}%</div>
        </div>
      </div>

      {questionType !== 'short_input' ? (
        <div
          className="relative min-h-[320px] rounded-lg border p-3"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--alt-bg)' }}
        >
          {!hasResponses && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-6 text-center">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-color)' }}>
                  {isPollActive ? 'Waiting for first response' : 'No responses showing for this question'}
                </p>
                <p className="mt-1 text-xs" style={{ color: 'var(--muted-color)' }}>
                  Results reset automatically when the next question is prepared.
                </p>
              </div>
            </div>
          )}
          <canvas ref={canvasRef} className="h-[300px] w-full" />
        </div>
      ) : (
        <div
          className="max-h-96 overflow-y-auto rounded-lg border"
          style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--alt-bg)' }}
        >
          <ul className="divide-y" style={{ borderColor: 'var(--muted-color)' }}>
            {Object.entries(answersData).length > 0 ? Object.entries(answersData).map(([answer, count]) => (
              <li
                key={answer}
                className="flex items-center justify-between px-3 py-2 text-sm"
                style={{
                  color: 'var(--text-color)',
                  borderColor: 'var(--muted-color)',
                }}
              >
                {answer}
                <span className="font-semibold">{count}</span>
              </li>
            )) : (
              <li className="px-3 py-8 text-center text-sm" style={{ color: 'var(--muted-color)' }}>
                {isPollActive ? 'Short answers will appear here as students submit.' : 'No short answers showing for this question.'}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default QuestionDisplay;
